import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import api from '../../api'
import { ProjectContext } from '../../contexts'
import { MonthlyUsage, ProjectUsage, Series, YearlyUsage } from '../../types'
import { DataTable, Heading } from '../../ui'
import Tile, { TileGrid } from '../../ui/Tile'
import Chart from '../../ui/Chart'
import { SingleSelect } from '../../ui/form/SingleSelect'

const CHANNEL_KEYS = ['email', 'push', 'text', 'webhook'] as const
type ChannelKey = typeof CHANNEL_KEYS[number]

const formatNumber = (value: number) => value.toLocaleString()

const formatMonth = (iso: string) => {
    const date = new Date(iso)
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        timeZone: 'UTC',
    })
}

const buildMonthlySeries = (monthly: MonthlyUsage[], t: (key: string) => string): Series[] => {
    const labels: Record<ChannelKey, string> = {
        email: t('email'),
        push: t('push'),
        text: t('text'),
        webhook: t('webhook'),
    }

    const seriesByLabel: Record<string, Series> = {}
    for (const c of CHANNEL_KEYS) {
        seriesByLabel[labels[c]] = { label: labels[c], data: [] }
    }

    for (const row of monthly) {
        const date = Date.parse(row.month)
        for (const c of CHANNEL_KEYS) {
            seriesByLabel[labels[c]].data.push({ date, count: row[c] })
        }
    }

    return CHANNEL_KEYS.map(c => seriesByLabel[labels[c]])
}

const compactFormatter = new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: 1,
})
const formatCompact = (value: number) => compactFormatter.format(value)

export default function Usage() {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const currentYear = new Date().getUTCFullYear()
    const [year, setYear] = useState<number>(currentYear)
    const [usage, setUsage] = useState<ProjectUsage | undefined>()
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        setIsLoading(true)
        api.projects.usage(project.id, year)
            .then(setUsage)
            .catch(() => setUsage(undefined))
            .finally(() => setIsLoading(false))
    }, [project.id, year])

    const yearOptions = usage
        ? usage.years.map(y => y.year).sort((a, b) => b - a)
        : [year]
    const monthlySeries = usage ? buildMonthlySeries(usage.monthly, t) : []
    const placeholder = '—'
    const tileValue = (value: number | undefined) =>
        value === undefined ? placeholder : formatNumber(value)

    return (
        <>
            <Heading title={t('usage')} size="h3">
                {t('usage_desc')}
            </Heading>

            <Heading
                title={t('monthly_breakdown')}
                size="h4"
                actions={
                    <SingleSelect
                        size="small"
                        options={yearOptions}
                        value={year}
                        onChange={(value: number | undefined) => value && setYear(value)}
                    />
                }
            />

            <TileGrid numColumns={4}>
                <Tile size="large" title={tileValue(usage?.yearly.email)}>{t('email')}</Tile>
                <Tile size="large" title={tileValue(usage?.yearly.push)}>{t('push')}</Tile>
                <Tile size="large" title={tileValue(usage?.yearly.text)}>{t('text')}</Tile>
                <Tile size="large" title={tileValue(usage?.yearly.webhook)}>{t('webhook')}</Tile>
            </TileGrid>

            <br />
            {usage && <Chart series={monthlySeries} dateFormat="MMM yyyy" axisFormatter={formatCompact} />}
            <br />

            <DataTable<MonthlyUsage>
                items={usage?.monthly}
                isLoading={isLoading}
                itemKey={({ item }) => item.month}
                columns={[
                    {
                        key: 'month',
                        title: t('month'),
                        cell: ({ item }) => formatMonth(item.month),
                    },
                    {
                        key: 'email',
                        title: t('email'),
                        align: 'end',
                        cell: ({ item }) => formatNumber(item.email),
                    },
                    {
                        key: 'push',
                        title: t('push'),
                        align: 'end',
                        cell: ({ item }) => formatNumber(item.push),
                    },
                    {
                        key: 'text',
                        title: t('text'),
                        align: 'end',
                        cell: ({ item }) => formatNumber(item.text),
                    },
                    {
                        key: 'webhook',
                        title: t('webhook'),
                        align: 'end',
                        cell: ({ item }) => formatNumber(item.webhook),
                    },
                    {
                        key: 'total',
                        title: t('total'),
                        align: 'end',
                        cell: ({ item }) => <strong>{formatNumber(item.total)}</strong>,
                    },
                ]}
            />

            <Heading title={t('yearly_totals')} size="h4" />
            <DataTable<YearlyUsage>
                items={usage ? [...usage.years].sort((a, b) => b.year - a.year) : undefined}
                isLoading={isLoading}
                itemKey={({ item }) => item.year}
                columns={[
                    {
                        key: 'year',
                        title: t('year'),
                        cell: ({ item }) => item.year,
                    },
                    {
                        key: 'email',
                        title: t('email'),
                        align: 'end',
                        cell: ({ item }) => formatNumber(item.email),
                    },
                    {
                        key: 'push',
                        title: t('push'),
                        align: 'end',
                        cell: ({ item }) => formatNumber(item.push),
                    },
                    {
                        key: 'text',
                        title: t('text'),
                        align: 'end',
                        cell: ({ item }) => formatNumber(item.text),
                    },
                    {
                        key: 'webhook',
                        title: t('webhook'),
                        align: 'end',
                        cell: ({ item }) => formatNumber(item.webhook),
                    },
                    {
                        key: 'total',
                        title: t('total'),
                        align: 'end',
                        cell: ({ item }) => <strong>{formatNumber(item.total)}</strong>,
                    },
                ]}
            />
        </>
    )
}
